import React, { ChangeEvent, useEffect, useState } from "react";
import Button from "src/components/button";
import { EditThumbnail } from "src/components/editThumbnail";
import { Crop } from "src/components/editThumbnail/crop";
import { MediaLibraryChooser } from "src/components/mediaLibrary";
import Slider from "src/components/slider";
import { MediaLibrary } from "src/types/mediaLibrary";
import styles from "./editBanner.module.scss";
import cx from "classnames";
import Actions from "src/components/actionbar";
import { POSITIONS } from "src/constants/banner";
import { animated, useTransition } from '@react-spring/web';
import { nanoid } from '@reduxjs/toolkit';
import { MediaFile } from "src/types/media";
import { updateStreamProperty } from "src/redux/actions/streams";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import _ from 'lodash';
import { parseMediaMetadata } from "src/services/media.service";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
interface EditBannerProps {
  stream: any;
  isEditBanner: boolean;
  onClose: (value: string, position: string) => void;
}

const EditBanner: React.FC<EditBannerProps> = ({ stream, isEditBanner, onClose }) => {

  const dispatch = useDispatch();
  const { 
    trackAddBannerClicked, trackBannerContentSelected, trackCropBannerClicked, trackBannerCropped,
    trackBannerAdded, trackBackgroundImageAdded
   } = useStreamsAdoption();

  const streamData = useSelector((state: any) => _.get(state, `stream.items[${stream?.id}].edit`));

  const [editAction, setEditAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [headerBounds, setHeaderBounds] = useState({ height: 0, width: 0 });
  const [cropData, setCropData] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [position, setPosition] = useState("center center");
  const [mediaType, setMediaType] = useState(_.get(stream, "banner.background.type"));

  const handleClick = (action: string) => {
    setEditAction(action);
    switch(action) {
      case "edit":
        trackAddBannerClicked();
        break;

      case "crop":
        trackCropBannerClicked({bannerType: mediaType})
        break;
    }
  }

  const handleThumbnailChange = (media: MediaLibrary) => {

    if (!media) {
      return;
    }

    setMediaType(media.mediaType);

    const imageUrl = media.imageUrl;
    if (!imageUrl) {
      return;
    }

    fetch(imageUrl)
      .then((response) => {
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);

        const backgroundMedia = {
          id: nanoid(6),
          entityType: 'media-preview',
          type: media.mediaType,
          full: url,
          thumbnail: url,
          width: media.width,
          height: media.height,
          contentType: blob.type,
        } as MediaFile

        dispatch(updateStreamProperty({
          id: stream.id,
          property: 'banner',
          value: {
            ...stream.banner,
            background: backgroundMedia,
            crop: null
          }
        }));

        setEditAction("");

        trackBannerContentSelected({bannerType: media.mediaType})

      }).catch(error => {
        console.error(error);
      });
  };


  const handleCroppedThumbnail = (value: { x: number, y: number, width: number, height: number }) => {
    setCropData(value);
  };


  const handlePositionChange = (value: string) => {
    setPosition(value);
  }

  const handleConfirm = () => {
    if (editAction === "crop") {
      setEditAction("");
      dispatch(updateStreamProperty({
        id: stream.id,
        property: 'banner',
        value: {
          ...streamData?.banner,
          crop: cropData
        }
      }))
      trackBannerCropped({bannerType: mediaType});
    } else if (editAction === "position") {
      setEditAction("");
    } else {
      onClose(streamData?.banner, position);
      trackBannerAdded({bannerType: mediaType});
      trackBackgroundImageAdded({bannerType: mediaType});
    }
  };

  const handleCancel = () => {
    if (editAction === "crop" || editAction === "position") {
      setEditAction("");
    } else {
      onClose("", position);
    }
  };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const thumbnailMedia = await parseMediaMetadata(file);
    if (!thumbnailMedia) return;

    setEditAction("");

    dispatch(updateStreamProperty({
      id: stream.id,
      property: 'banner',
      value: {
        ...stream.banner,
        background: thumbnailMedia,
        crop: null
      }
    }))
  }

  const accept = "image/*, video/*";
  const disabled = false;

  return (
    <Slider.Dialog
      show={isEditBanner}
      width={editAction === "edit" ? `33.25rem` : `23.75rem`}
      onClose={() => onClose(streamData?.banner, position)}
    >
      <Slider.Header
        title={
          <>
            {editAction === "edit" && (
              <i
                className={cx("paperflite-left-arrow", styles.backIcon)}
                onClick={() => setEditAction("")}
              ></i>
            )}
            <span>make it pretty, please</span>

            {editAction === "edit" && (
              <button className={cx(styles.fileUpload)} disabled={false}>
                <i className={cx(styles.icon, "paperflite-upload")}></i>
                <span className={styles.title}>upload</span>
                <input
                  type="file"
                  accept={accept}
                  disabled={disabled}
                  onChange={handleFileChange}
                ></input>
              </button>
            )}
          </>
        }
        message="Choose an image that fits just right—like finding perfect jeans"
      />
      <Slider.Body>
        {(editAction === "" || editAction === "position") && (
          <EditThumbnail
            show={true}
            isTransition={false}
            icon={_.get(streamData, "banner", _.get(stream, "banner"))}
            entityType="stream"
            handleClick={handleClick}
            position={position}
            showToolbar={editAction !== "position"}
            mediaType={_.get(
              streamData,
              "banner.background.type",
              _.get(stream, "banner.background.type"),
            )}
          />
        )}

        {editAction === "edit" && (
          <div>
            <MediaLibraryChooser
              entityType="stream"
              onChange={handleThumbnailChange}
              columns={4}
            />
          </div>
        )}

        {editAction === "crop" && (
          <Crop 
            enabled={mediaType ? mediaType !== "video" : true}
            icon={streamData?.banner.background.full || stream?.banner.background.full}
            loading={loading}
            onCrop={handleCroppedThumbnail}
            headerDimension={{
              height: headerBounds.height,
              width: headerBounds.width,
            }}
            cropperDimensions={{
              width: "15rem",
              height: "19.375rem",
            }}
          />
        )}

        {editAction === "position" && (
          <BannerPositionMenu handlePositionChange={handlePositionChange} />
        )}
      </Slider.Body>

      {editAction !== "edit" && (
        <Slider.Actions>
          <Button
            icon="paperflite-tick"
            text={
              editAction === "crop" || editAction === "position"
                ? "Save"
                : "Confirm"
            }
            variant="primary"
            onClick={handleConfirm}
          />
          <Button text="Cancel" variant="secondary" onClick={handleCancel} />
        </Slider.Actions>
      )}
    </Slider.Dialog>
  );
}

export { EditBanner };

interface BannerPositionMenuProps {
  handlePositionChange: (value: string) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ handlePositionChange }) => {
  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
    delay: 500,
  });

  return transitions((style, item) =>
    item ? (
      <animated.div style={style} className={styles.positionMenu}>
        <Actions.Container>
          <Actions.Row className={styles.positionActionRow}>
            <Actions.Item className={styles.positions}>
              {POSITIONS.map((position) => {
                return (
                  <div
                    key={position.value}
                    className={cx(styles.position)}
                    onClick={() => handlePositionChange(position.value)}
                  />
                );
              })}
            </Actions.Item>
          </Actions.Row>
        </Actions.Container>
      </animated.div>
    ) : null
  );
};